// Image library for the email builder: upload (click, drag and drop, or paste), reuse earlier uploads,
// pick a Shopify product photo, or link an image by URL. Resolves with the chosen image's URL.
import { useEffect, useRef, useState } from 'react';
import { fetchAssets, uploadAsset, deleteAsset } from '../../api/marketing.js';
import { fetchLookbookProducts } from '../../api/lookbooks.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

/** Upload a file and return the asset (shared with canvas drag-and-drop). */
export async function uploadImageFile(file) {
  if (!file?.type?.startsWith('image/')) throw new Error('That isn’t an image.');
  return uploadAsset(file);
}

export default function ImagePicker({ store, onPick, onClose }) {
  const [tab, setTab] = useState('uploads');
  const [assets, setAssets] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [drag, setDrag] = useState(false);
  const [q, setQ] = useState('');
  const [products, setProducts] = useState(null);
  const [url, setUrl] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    fetchAssets().then(setAssets).catch(() => setAssets([]));
  }, []);
  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  useEffect(() => {
    if (tab !== 'products') return undefined;
    setProducts(null);
    const t = setTimeout(() => {
      fetchLookbookProducts({ store, q })
        .then((r) => setProducts((r.products || []).filter((p) => p.image)))
        .catch(() => setProducts([]));
    }, 300);
    return () => clearTimeout(t);
  }, [tab, q, store]);

  const upload = async (files) => {
    const list = [...(files || [])].filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    setBusy(true);
    setErr('');
    try {
      const done = [];
      for (const f of list) done.push(await uploadAsset(f));
      setAssets((a) => [...done.reverse(), ...(a || [])]);
      if (done.length === 1) onPick(done[0].url, { alt: done[0].name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ') });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    const h = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (files.length) {
        e.preventDefault();
        upload(files);
      }
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  });

  const tabBtn = (k, l) => (
    <button type="button" onClick={() => setTab(k)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
      {l}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-surface shadow-lg" role="dialog" aria-label="Choose an image">
        <div className="flex items-center justify-between border-b border-line px-4 pt-3">
          <div className="flex gap-1">
            {tabBtn('uploads', 'Your images')}
            {tabBtn('products', 'Product photos')}
            {tabBtn('link', 'Link')}
          </div>
          <button onClick={onClose} className="mb-2 rounded px-2 py-1 text-text-muted hover:bg-surface-raised" aria-label="Close">✕</button>
        </div>
        <div className="min-h-[320px] overflow-y-auto p-4">
          {err && <p className="mb-2 rounded bg-error-light px-3 py-2 text-sm text-error">{err}</p>}
          {tab === 'uploads' && (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  upload(e.dataTransfer.files);
                }}
                onClick={() => fileRef.current?.click()}
                className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center ${drag ? 'border-accent bg-accent-light' : 'border-line hover:bg-surface-raised'}`}
              >
                <div className="text-sm font-medium text-text-primary">{busy ? 'Uploading…' : 'Drop images here, paste, or click to upload'}</div>
                <div className="mt-1 text-xs text-text-muted">JPG, PNG, GIF or WebP up to 15 MB — resized and compressed for email automatically</div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {(assets || []).map((a) => (
                  <div key={a.id} className="group relative">
                    <button type="button" onClick={() => onPick(a.url, { alt: a.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ') })} className="block w-full overflow-hidden rounded-md border border-line bg-surface-raised hover:border-accent">
                      <img src={a.url} alt={a.name} className="aspect-square w-full object-contain" loading="lazy" />
                    </button>
                    <div className="mt-1 truncate text-[11px] text-text-muted" title={a.name}>
                      {a.width ? `${a.width}×${a.height} · ` : ''}
                      {kb(a.size)}
                    </div>
                    <button
                      type="button"
                      title="Delete image"
                      onClick={async () => {
                        if (!window.confirm(`Delete “${a.name}”? Drafts using it will show a missing image.`)) return;
                        try {
                          await deleteAsset(a.id);
                          setAssets((l) => l.filter((x) => x.id !== a.id));
                        } catch (e) {
                          setErr(errMsg(e));
                        }
                      }}
                      className="absolute right-1 top-1 hidden rounded bg-white/90 px-1.5 text-xs text-error shadow-sm group-hover:block"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              {assets && !assets.length && <p className="mt-4 text-center text-sm text-text-muted">No images yet — upload your first one above.</p>}
            </>
          )}
          {tab === 'products' && (
            <>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
              {!products ? (
                <p className="mt-4 text-sm text-text-muted">Loading…</p>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {products.map((p) => (
                    <button key={p.id} type="button" onClick={() => onPick(p.image, { alt: p.title, product: p })} className="text-left">
                      <img src={p.image} alt={p.title} className="aspect-square w-full rounded-md border border-line object-cover hover:border-accent" loading="lazy" />
                      <div className="mt-1 line-clamp-2 text-[11px] text-text-secondary">{p.title}</div>
                    </button>
                  ))}
                  {!products.length && <p className="col-span-full text-sm text-text-muted">No products found.</p>}
                </div>
              )}
            </>
          )}
          {tab === 'link' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (/^https:\/\//i.test(url.trim())) onPick(url.trim(), {});
                else setErr('Use a full https:// image address.');
              }}
              className="space-y-3"
            >
              <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://cdn.shopify.com/…/image.jpg" className="w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
              {/^https:\/\//i.test(url) && <img src={url} alt="" className="max-h-56 rounded-md border border-line" />}
              <button className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white">Use this image</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
